import React from 'react';
import { useField } from 'formik';

import type { SurveyRatingConfig } from '@/api/surveys';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface FormRatingFieldProps {
    name: string;
    label?: string;
    helpText?: string;
    required?: boolean;
    disabled?: boolean;
    config?: SurveyRatingConfig;
    validate?: (value: unknown) => string | undefined;
}

const DEFAULT_RATING_CONFIG: SurveyRatingConfig = {
    min: 1,
    max: 5,
    step: 1,
};

const buildRatingValues = (config: SurveyRatingConfig) => {
    const values: number[] = [];
    for (let current = config.min; current <= config.max; current += config.step) {
        values.push(Number(current.toFixed(4)));
    }
    return values;
};

export const FormRatingField: React.FC<FormRatingFieldProps> = ({
    name,
    label,
    helpText,
    required,
    disabled,
    config = DEFAULT_RATING_CONFIG,
    validate,
}) => {
    const validateFn = (value: unknown) => {
        if (required && (value === undefined || value === null || value === '')) {
            return 'Campo obrigatório';
        }

        if (validate) return validate(value);
        return undefined;
    };

    const [field, meta, helpers] = useField({ name, validate: validateFn });
    const values = buildRatingValues(config);
    const selectedValue = field.value === '' ? null : Number(field.value);

    return (
        <div className="space-y-3">
            {label && (
                <div className="space-y-1">
                    <Label className={cn(meta.touched && meta.error && 'text-destructive')}>
                        {label} {required ? '*' : ''}
                    </Label>
                    {helpText ? (
                        <p className="text-sm leading-relaxed text-muted-foreground">{helpText}</p>
                    ) : null}
                </div>
            )}

            <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                    {values.map((value) => {
                        const labelForValue = config.value_labels?.[String(value)] ?? value;
                        const isSelected = selectedValue === value;

                        return (
                            <Button
                                key={value}
                                type="button"
                                variant={isSelected ? 'default' : 'outline'}
                                size="sm"
                                disabled={disabled}
                                className="min-w-11 rounded-full"
                                onClick={() => helpers.setValue(value)}
                            >
                                {labelForValue}
                            </Button>
                        );
                    })}
                </div>
                {(config.left_label || config.right_label) && (
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{config.left_label}</span>
                        <span>{config.right_label}</span>
                    </div>
                )}
            </div>

            {meta.touched && meta.error ? (
                <p className="text-[0.8rem] font-medium text-destructive">{meta.error}</p>
            ) : null}
        </div>
    );
};
